import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  CONSENT_STORAGE_KEY,
  updateGoogleConsent,
} from '@/lib/analytics';

export { CONSENT_STORAGE_KEY };
export const COOKIE_SETTINGS_EVENT = 'fixtyer:open-cookie-settings';

type ConsentChoice = 'analytics' | 'essential';

function applyConsent(choice: ConsentChoice): void {
  updateGoogleConsent(choice);
}

function storedChoice(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'analytics' || value === 'essential' ? value : null;
  } catch {
    return null;
  }
}

function storeChoice(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Consent still applies for this page even when browser storage is blocked.
  }
}

export function CookieConsent() {
  const [choice, setChoice] = useState<ConsentChoice | null | undefined>(undefined);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const savedChoice = storedChoice();
    setChoice(savedChoice);
    if (savedChoice) applyConsent(savedChoice);

    const openSettings = () => setSettingsOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  const saveChoice = (nextChoice: ConsentChoice) => {
    storeChoice(nextChoice);
    applyConsent(nextChoice);
    setChoice(nextChoice);
    setSettingsOpen(false);
  };

  if (choice === undefined || (choice !== null && !settingsOpen)) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6">
      <section
        role="region"
        aria-labelledby="cookie-preferences-title"
        aria-describedby="cookie-preferences-description"
        className="mx-auto max-w-3xl rounded-2xl border border-surface-200 bg-white p-6 shadow-2xl"
      >
        <h2 id="cookie-preferences-title" className="font-display text-xl font-semibold text-brand-950">
          Cookie preferences
        </h2>
        <p id="cookie-preferences-description" className="mt-2 text-sm leading-relaxed text-surface-700">
          Essential storage keeps Fixtyer working and is always available. With your permission,
          Google Analytics and advertising storage help us understand and measure use of the site.
          They stay denied unless you accept analytics and advertising.
        </p>
        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link
            to="/cookies"
            className="inline-flex min-h-11 items-center justify-center px-4 text-sm font-medium text-brand-700 hover:text-brand-900"
          >
            Read cookie policy
          </Link>
          <button
            type="button"
            onClick={() => saveChoice('essential')}
            className="min-h-11 rounded-lg border border-brand-200 px-5 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => saveChoice('analytics')}
            className="min-h-11 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Accept analytics and advertising
          </button>
        </div>
      </section>
    </div>
  );
}
